/**
 * Wraps a link or button and opens the Gorgias chat widget on click instead of letting
 * it navigate, while leaving the wrapped control's own href/behavior as the fallback for
 * when the widget is unavailable (app embed disabled, script blocked, consent not given).
 *
 * Shared by the live-chat-button block and the footer menu's "live chat" link so the
 * chat-open logic, the fallback, and the early-click race below only exist once.
 */
class LiveChatButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#watchForChat();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    clearTimeout(this.timer);
  }

  /**
   * The app embed injects the widget asynchronously, so window.GorgiasChat is
   * usually absent at connect time. Poll for it instead of reading it once, or an
   * early click falls through to the fallback link even though chat is coming.
   *
   * Gorgias's own object throws ("...before its initialization...") on property
   * access - not just returns undefined - for every property but `init` until its
   * bundle finishes loading, well after `window.GorgiasChat` itself exists. An
   * unguarded `typeof window.GorgiasChat.open` during that window throws out of
   * this poll and silently kills the recursive setTimeout chain after a single
   * attempt, so #isChatOpenable below must swallow that.
   */
  #watchForChat(attempt = 0) {
    if (this.#isChatOpenable(window.GorgiasChat)) {
      this.chat = window.GorgiasChat;
      this.dataset.chatReady = 'true';
      this.#establishAvailability();
      return;
    }

    if (attempt >= 40) return; // ~20s, then assume the embed is not installed.
    this.timer = setTimeout(() => this.#watchForChat(attempt + 1), 500);
  }

  #isChatOpenable(chat) {
    try {
      return !!chat && typeof chat.open === 'function';
    } catch {
      return false;
    }
  }

  /**
   * GorgiasChat.isChatOnline() reflects the store's real availability (agent
   * presence gated by configured business hours - confirmed by forcing custom
   * business hours and watching it flip), but Gorgias emits no event for it
   * changing, so there is nothing to subscribe to: read it once, right after
   * the readiness check above proves the widget is past the init-throw window.
   * Never guess: if the call throws or doesn't return a boolean, the status
   * dot stays hidden (its CSS default) rather than showing a wrong color.
   */
  #establishAvailability() {
    let online;
    try {
      online = this.chat.isChatOnline();
    } catch {
      return;
    }
    if (typeof online !== 'boolean') return;

    this.dataset.availability = online ? 'online' : 'offline';
    const label = online ? this.dataset.labelOnline : this.dataset.labelOffline;
    if (!label) return;
    this.querySelectorAll('[data-live-chat-status-text]').forEach((el) => {
      el.textContent = label;
    });
  }

  #handleClick = (event) => {
    const control = event.target.closest('a, button');
    if (!control || !this.contains(control)) return;

    const chat = this.chat || window.GorgiasChat;
    if (!this.#isChatOpenable(chat)) return;

    // Only take over navigation when the widget can actually handle it. Otherwise
    // let the control's own click behavior (its href) run as the fallback.
    event.preventDefault();
    chat.open();
  };
}

if (!customElements.get('live-chat-button')) {
  customElements.define('live-chat-button', LiveChatButton);
}
